import { Suspense } from 'react';

import { Constants } from '@constants';
import {
    IconExclamationTriangleFilled,
    IconInfoCircleOutline
} from '@iwoca/icons';
import { AlertBox, Text } from '@iwoca/orion-ui';
import { NEW_IwLinkButton } from '@iwRouter/NEW_IwLinkButton';
import { FormattedMessage } from 'react-intl';

import { useGetDeliveryAddressData } from './useGetDeliveryAddressData';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { ACCOUNT_CONFIRMATION_URL } from '../../constants';

export const ConfirmDeliveryAddress = () => (
    <>
        <div className="my-2xl">
            <Text className="text-3xl font-bold">
                <FormattedMessage
                    id="_.credit_card.onboarding.delivery.title"
                    defaultMessage="All signed! One last thing..."
                />
            </Text>
            <Text className="my-m text-xl font-reg">
                <FormattedMessage
                    id="_.credit_card.onboarding.delivery.description"
                    defaultMessage="We'll be sending your card to the address below. Hit confirm to activate and send off your card."
                />
            </Text>
        </div>

        <ResidentialAddress />
    </>
);

const ResidentialAddress = () => {
    const { residential_address } = useGetDeliveryAddressData();
    if (!residential_address) {
        return (
            <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                <Suspense fallback={<MainContentPending />}>
                    <Text className="mb-m mt-xl flex flex-row gap-l text-m font-bold">
                        <IconExclamationTriangleFilled
                            className="my-auto fill-error"
                            fontSize={20}
                        />
                        <FormattedMessage
                            defaultMessage="Unverified address"
                            id="_.credit_card.onboarding.delivery.address.error"
                        />
                    </Text>
                    <Text className="text-m font-reg">
                        <FormattedMessage
                            defaultMessage="We're unable to verify your residential address. Please call us on <a>{phone}</a>"
                            id="_.credit_card.onboarding.delivery.address.error.description"
                            values={{
                                phone: Constants.PHONE_NUMBER.display,
                                a: (chunks) => (
                                    <a
                                        href={`tel:${Constants.PHONE_NUMBER.tel}`}
                                    >
                                        <strong>{chunks}</strong>
                                    </a>
                                )
                            }}
                        />
                    </Text>
                </Suspense>
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense fallback={<MainContentPending />}>
                <AlertBox className="my-auto flex flex-row gap-s">
                    <IconInfoCircleOutline className="my-auto" />
                    <Text className="text-s font-med">
                        <FormattedMessage
                            defaultMessage="Your virtual card will be available to use immediately."
                            id="_.credit_card.onboarding.delivery.info"
                        />
                    </Text>
                </AlertBox>
                <div className="my-3xl flex flex-col">
                    <Text className="text-l font-bold">
                        <FormattedMessage
                            defaultMessage="ID verified address"
                            id="_.credit_card.onboarding.delivery.address_title"
                        />
                    </Text>
                    <Text className="text-m font-reg">
                        {residential_address?.house_number}
                        {residential_address?.house_number &&
                            residential_address?.street_line_1 &&
                            /* eslint-disable-next-line formatjs/no-literal-string-in-jsx */
                            ' '}
                        {residential_address?.street_line_1}
                        {residential_address?.street_line_1 &&
                            residential_address?.town &&
                            /* eslint-disable-next-line formatjs/no-literal-string-in-jsx */
                            ', '}
                        {residential_address?.town}
                    </Text>
                    <Text className="text-m font-reg">
                        {residential_address?.postcode}
                    </Text>
                </div>

                <NEW_IwLinkButton
                    variant="primary"
                    to={{ pathname: ACCOUNT_CONFIRMATION_URL }}
                >
                    <Text className="text-m font-bold text-structure-100">
                        <FormattedMessage
                            id="_.credit_card.onboarding.delivery.confirm_and_activate"
                            defaultMessage="Confirm and activate card"
                        />
                    </Text>
                </NEW_IwLinkButton>
            </Suspense>
        </ErrorBoundary>
    );
};
